import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { from, Observable, of, timer } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Agent } from '../../../domain/interfaces/agents';
import { catchError, concatMap, groupBy, map, mergeMap, share, tap, toArray } from 'rxjs/operators';
import { LoginService } from '@uoa/auth';
import peelPage from '../../../core/pipes/peel-first-page.operator';
import { NgClass, AsyncPipe } from '@angular/common';
import { StatusSummaryComponent } from '../../wallboard/status-summary/status-summary.component';
import { CardFrameComponent } from '../../wallboard/card-frame/card-frame.component';
import { AgentCardComponent } from '../../wallboard/agent-card/agent-card.component';

interface StatsQueue {
  queueName: string;
  oldestContact?: number | string;
  oldestContactOverThreshold?: boolean;
  [key: string]: unknown;
}

interface StatsSummary {
  callsWaiting?: number;
  GOS?: number;
  [key: string]: unknown;
}

interface StatsApiResponse {
  data: StatsQueue[];
  dataSummary: StatsSummary;
  emailReportData?: {
    rows?: number[][];
  };
}

interface StatsViewModel {
  data: StatsQueue[];
  dataSummary: StatsSummary & {
    callsWaitingOverThreshold: boolean;
    gosOverThreshold: boolean;
  };
  totalEmails: number;
}

interface AgentsApiResponse {
  data: {
    Items: Agent[];
  };
}

@Component({
  selector: 'app-stats-board',
  templateUrl: './stats-board.component.html',
  styleUrls: ['./stats-board.page1.component.scss'],
  imports: [NgClass, StatusSummaryComponent, CardFrameComponent, AgentCardComponent, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class StatsBoardComponent implements OnInit {
  private httpClient = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);

  stats$: Observable<StatsViewModel>;
  statsTitle: string;
  agentsTitle: string;

  agents$: Observable<Agent[][]>;
  agentsPage1$: Observable<Agent[][]>;
  agentsPage2$: Observable<Agent[][]>;
  agentsPage3$: Observable<Agent[][]>;
  agentsPage4$: Observable<Agent[][]>;

  available = 0;
  notAvailable = 0;
  outOfOffice = 0;

  slideOpts = {
    speed: 1000,
    autoplay: {
      delay: 20000,
    },
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
  };

  dateCompare(a: Agent, b: Agent): number {
    return new Date(a.CurrentEvent.Date).getTime() - new Date(b.CurrentEvent.Date).getTime();
  }

  ngOnInit() {
    this.route.data.subscribe((api) => {
      console.log('Got API: ', api);

      if (environment.wallboards.staff.stats.mockData) {
        console.log('fake api');
        this.stats$ = of(environment.wallboards.staff.stats.mockData).pipe(map(this.mapStats));
        console.log('Using stats mock data: ', environment.wallboards.staff.stats.mockData);
        this.statsTitle = api.stats?.title;
      } else {
        console.log('real api');
        let options = {};
        let urlSuffix = '';
        const apikey = this.route.snapshot.params['key'];
        if (apikey) {
          options = {
            headers: {
              'x-api-key': apikey,
            },
          };
          urlSuffix = '-tv';
        }
        this.statsTitle = api.stats?.title;
        // soft reload timer
        this.stats$ = timer(0, 4000).pipe(
          // workaround until auth library can handle `x-amzn-errortype: UnauthorizedException` header
          tap(async () => {
            if (!apikey) {
              await this.loginService.doLogin(this.router.url);
            }
          }),
          concatMap(() =>
            this.httpClient.get<StatsApiResponse>(api.stats.baseUrl + urlSuffix, options).pipe(
              tap((s) => {
                console.log('Got stats data: ', s);
                console.log('Reloading', s);
              }),
              map(this.mapStats),
              tap((s) => console.log('s', s)),

              tap(async () => {
                // Get logged in user
                const user = await this.loginService.getUserInfo();
                console.log('User: ', user);
              }),
              catchError((error) => {
                if (error.status === 429) {
                  console.warn('API is rate limited. You may need to adjust the rate limiting threshold.');
                  return of({ data: [], dataSummary: {}, totalEmails: 0 } as StatsViewModel);
                }
                return of({ data: [], dataSummary: {}, totalEmails: 0 } as StatsViewModel);
              }),
            ),
          ),
        );
      }

      if (environment.wallboards.staff.agents.mockData) {
        const mockAgentsResponse = environment.wallboards.staff.agents.mockData as unknown as AgentsApiResponse;
        console.log('Using agents mock data: ', mockAgentsResponse);
        // ascending by status, descending by time
        this.agents$ = of(mockAgentsResponse).pipe(
          // agents
          tap(this.tapAgents),
          map(this.filterAgents),
          // split array into separate emitted objects
          mergeMap((s: Agent[]) => from(s)),
          // group objects together by identifier
          groupBy((s: Agent) => s.CurrentEvent.Status.Item.GroupName),
          // convert each group to arrays
          mergeMap((group) => group.pipe(toArray())),
          // sort within each group: date descending
          //tap(c => console.info(c)),
          map((agentsInGroup: Agent[]) => agentsInGroup.sort(this.dateCompare)),
          // do the opposite of the split above, and emit a single array (of arrays)
          toArray(),

          catchError(() => of([])),
        );
        this.subscribeAgents();
      } else {
        let options = {};
        let urlSuffix = '';
        const apikey = this.route.snapshot.params['key'];
        if (apikey) {
          options = {
            headers: {
              'x-api-key': apikey,
            },
          };
          urlSuffix = '-tv';
        }
        console.log('Getting agents');
        // soft reload timer
        this.agents$ = timer(0, 8000).pipe(
          // workaround until auth library can handle `x-amzn-errortype: UnauthorizedException` header
          tap(async () => {
            if (!apikey) {
              await this.loginService.doLogin(this.router.url);
            }
          }),
          concatMap(() =>
            this.httpClient.get<AgentsApiResponse>(api.agents.baseUrl + urlSuffix, options).pipe(
              // agents
              tap(this.tapAgents),
              map(this.filterAgents),
              // split array into separate emitted objects
              mergeMap((s: Agent[]) => from(s)),
              // group objects together by identifier
              groupBy((s: Agent) => s.CurrentEvent.Status.Item.GroupName),
              // convert each group to arrays
              mergeMap((group) => group.pipe(toArray())),
              // sort within each group: date descending
              //tap(c => console.info(c)),
              map((agentsInGroup: Agent[]) => agentsInGroup.sort(this.dateCompare)),
              // do the opposite of the split above, and emit a single array (of arrays)
              toArray(),

              catchError(() => of([])),
            ),
          ),
        );

        this.subscribeAgents();
      }

      // hard reload timer in case the above timer gets jammed throughout the day
      window.setTimeout(
        () => {
          window.location.reload();
        },
        1000 * 60 * 60,
      );
    });
  }

  mapStats = (s: StatsApiResponse): StatsViewModel => ({
    data: s.data
      .filter((n) => !n.queueName.toLowerCase().includes('unused'))
      .map((q) => {
        const oldestContactSeconds = Number(q.oldestContact ?? 0);
        const hasOldestContact = Number.isFinite(oldestContactSeconds) && oldestContactSeconds > 0;
        const oldestContactOverThreshold = q.oldestContactOverThreshold ? q.oldestContactOverThreshold : oldestContactSeconds >= 3600;

        return {
          ...q,
          oldestContactOverThreshold,
          oldestContact: hasOldestContact ? this.formatDuration(oldestContactSeconds, oldestContactOverThreshold) : '-',
          queueName: q.queueName.replace('Queue', ''),
        };
      }),
    dataSummary: {
      ...s.dataSummary,
      callsWaitingOverThreshold: s.dataSummary?.callsWaiting > 5,
      gosOverThreshold: s.dataSummary?.GOS < 80 && s.dataSummary?.GOS >= 0.01,
    },
    totalEmails: s.emailReportData?.rows ? s.emailReportData.rows[0][0] : 0,
  });

  filterAgents = (s: AgentsApiResponse): Agent[] => {
    return s.data.Items.filter((item) => item.FullAgentName && !item.FullAgentName.includes('IAM'));
  };

  tapAgents = (s: AgentsApiResponse): void => {
    console.log('Got agent data: ', s);
    console.log('Updating board using', s.data?.Items);
    this.available = s.data?.Items.filter(
      (item) => item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() === 'green' /*&&
        (item.CurrentEvent?.Status?.Item?.Visible !== true || item.CurrentEvent?.Status?.Item?.Visible !== 'true')*/,
    ).length;
    this.notAvailable = s.data?.Items.filter(
      (item) => item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() === 'orange' /*&&
        (item.CurrentEvent?.Status?.Item?.Visible !== true || item.CurrentEvent?.Status?.Item?.Visible !== 'true')*/,
    ).length;
    this.outOfOffice = s.data?.Items.filter(
      (item) => item.CurrentEvent?.Status?.Item?.Colour.toLowerCase() === 'red' /*&&
        (item.CurrentEvent?.Status?.Item?.Visible !== true || item.CurrentEvent?.Status?.Item?.Visible !== 'true')*/,
    ).length;
  };

  subscribeAgents = () => {
    this.agents$.subscribe((agentGroups) => console.log('Agent groups: ', agentGroups));

    // avoid doing http call multiple times
    const sharedAgentGroups = this.agents$.pipe(share());

    this.agentsPage1$ = sharedAgentGroups.pipe((s) => peelPage(s, 1));
    this.agentsPage2$ = sharedAgentGroups.pipe((s) => peelPage(s, 2));
    this.agentsPage3$ = sharedAgentGroups.pipe((s) => peelPage(s, 3));
    this.agentsPage4$ = sharedAgentGroups.pipe((s) => peelPage(s, 4));
  };

  private formatDuration(totalSeconds: number, useHourFormat: boolean): string {
    const seconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainderSeconds = seconds % 60;

    if (useHourFormat) {
      return `${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}:${`${remainderSeconds}`.padStart(2, '0')}`;
    }

    const totalMinutes = Math.floor(seconds / 60);
    return `${`${totalMinutes}`.padStart(2, '0')}:${`${remainderSeconds}`.padStart(2, '0')}`;
  }
}
